import { useMemo, useState } from "react";
import { useNavigate } from "react-router";

import {
  Badge,
  BadgeColor,
  Button,
  ButtonStyle,
  ConfirmModal,
  DetailCardSection,
  Modal,
} from "@prequel/shared-components";

import { useDeleteProvider, useUpdateProvider } from "@/api/import/providers";
import DangerZone from "@/components/DangerZone";
import { useProviderContext } from "@/pages/import/ProvidersPage/ProviderPage";

const ProviderAdmin = () => {
  const { provider } = useProviderContext();
  const navigate = useNavigate();
  const updateProvider = useUpdateProvider();
  const deleteProvider = useDeleteProvider();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const onConfirmStatusChange = () => {
    setShowStatusModal(false);
    updateProvider.mutate({ id: provider.id, provider: { is_enabled: !provider.is_enabled }, redirect: false });
  };

  const onConfirmDelete = () => {
    setShowDeleteModal(false);
    deleteProvider.mutate(provider.id);
  };

  const dangerZoneActions = useMemo(
    () => [
      {
        label: "Delete provider",
        description:
          "This action cannot be undone. All associated Sources, Datasets, and Streams must be deleted before this Provider can be deleted.",
        buttonText: "Delete provider",
        onClick: () => setShowDeleteModal(true),
      },
    ],
    [],
  );

  return (
    <>
      <Modal open={showStatusModal} onClose={() => setShowStatusModal(false)}>
        <ConfirmModal
          title={provider.is_enabled ? "Disable provider" : "Enable provider"}
          message={
            provider.is_enabled
              ? "Disabling pauses all activity for this provider until it is re-enabled. Existing data is retained."
              : "Enabling will provision compute resources for all applicable provider-isolated data flow."
          }
          onConfirm={onConfirmStatusChange}
          confirmButtonText={provider.is_enabled ? "Disable" : "Enable"}
          onCancel={() => setShowStatusModal(false)}
        />
      </Modal>

      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <ConfirmModal
          title="Delete provider"
          message="Are you sure you want to delete this provider? This action cannot be undone."
          onConfirm={onConfirmDelete}
          confirmButtonText="Delete"
          onCancel={() => setShowDeleteModal(false)}
        />
      </Modal>

      <DetailCardSection title="Edit provider">
        <p className="mb-3 text-sm text-stone-600">Update the provider&apos;s name and assigned products.</p>
        <Button
          text="Edit provider"
          type={ButtonStyle.SECONDARY}
          onClick={() => navigate(`/import/providers/${provider.id}/edit`)}
        />
      </DetailCardSection>

      <DetailCardSection title="Provider status">
        <p className="mb-3 text-sm text-stone-600">
          Enabling a Provider will provision compute resources for all applicable Provider-isolated data flow. Disabling
          pauses all activity for this Provider until re-enabled. Existing data is retained.
        </p>
        <div className="flex items-center gap-3">
          <Button
            text="Edit status"
            type={ButtonStyle.SECONDARY}
            onClick={() => setShowStatusModal(true)}
            disabled={updateProvider.isPending}
          />
          <Badge
            text={provider.is_enabled ? "Enabled" : "Disabled"}
            color={provider.is_enabled ? BadgeColor.GREEN : BadgeColor.GRAY}
            active
          />
        </div>
      </DetailCardSection>

      <DangerZone actions={dangerZoneActions} type="card" />
    </>
  );
};

export default ProviderAdmin;
